import { useAuth0 } from "@auth0/auth0-react";
import { TableContainer } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Pagination from "@material-ui/lab/Pagination";
import { ReactComponent as PlusIcon } from "assets/icons/plus.svg";
import Button from "components/button/Button";
import AlertDialog from "components/Dialog";
import RightDrawer from "components/drawer";
import MyModal from "components/MyModal";
import TableColumnFilter from "components/TableColumnFilter/TableColumnFilter";
import PastoralService from "Lib/API/PastoralApi/index";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toastr } from "react-redux-toastr";
import AddGroup from "./addGroup/index";
import AddNewReport from "./addNewReport";
import "./index.scss";
import { activeOptions } from "./scheduleReportConstant";
import ScheduleReportTableBody from "./ScheduleReportTableBody";
import ViewGroup from "./viewGroup";
import { fontConfig } from "theme/fontConfig";

const TableCellHead = withStyles((theme) => ({
	root: {
		height: "39px",
		padding: "0px 20px",
		borderBottom: "none",
		fontFamily: fontConfig.GeneralTableCellHeadFont.family,
		paddingLeft: "20px !important",
	},
}))(TableCell);

const ScheduleReports = ({ eventTypes, getEventTypesLoading }) => {
	const { getAccessTokenSilently } = useAuth0();

	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [reportListing, setReportListing] = useState([]);
	const [scheduleReportTotal, setScheduleReportTotal] = useState(0);
	const [openCreateScheduleModal, setOpenCreateScheduleModal] = useState(false);
	const [selectedSchedule, setSelectedSchedule] = useState(null);
	const [openViewGroupDrawer, setOpenViewGroupDrawer] = useState(false);
	const [openAddGroupModal, setOpenAddGroupModal] = useState(false);
	const [getGroupsLoading, setGetGroupsLoading] = useState(false);
	const [getFrequencyLoading, setGetFrequencyLoading] = useState(false);
	const [frequencyList, setFrequencyList] = useState([]);
	const [groupsList, setGroupsList] = useState([]);
	const [selectedGroup, setSelectedGroup] = useState(null);
	const [confirmClose, setConfirmClose] = useState(false);

	const [openDeleteConfirmModal, setOpenDeleteConfirmModal] = useState({
		open: false,
		type: "",
		message: "",
		description: "",
		isLoading: false,
	});
	const [selectedFilter, setSelectedFilter] = useState({
		eventType: [],
		group: [],
		active: [],
	});
	const [selectedFilterAll, setSelectedFilterAll] = useState({
		eventType: false,
		group: false,
		active: false,
	});

	const getGroupsHandler = async () => {
		setGetGroupsLoading(true);
		const token = await getAccessTokenSilently();
		PastoralService.getGroups({ token, page: 1 })
			.then((res) => {
				if (res) {
					const formattedGroups = res?.groups?.map((item) => ({
						label: item?.name,
						value: item?.id,
						memo: item?.memo,
					}));
					setGroupsList(formattedGroups);
				}
			})
			.catch((err) => {
				toastr.error("Error", err?.data?.message || "Something went wrong");
			})
			.finally(() => {
				setGetGroupsLoading(false);
			});
	};

	const getFrequencyHandler = async () => {
		setGetFrequencyLoading(true);
		const token = await getAccessTokenSilently();
		PastoralService.getFrequency({ token, page:1 })
			.then((res) => {
				if (res) {
					const formattedFrequency = res?.frequencies?.map((item) => ({
						label: item?.frequency,
						value: item?.id,
					}));
					setFrequencyList(formattedFrequency);
				}
			})
			.catch((err) => {
				toastr.error("Error", err?.data?.message || "Something went wrong");
			})
			.finally(() => {
				setGetFrequencyLoading(false);
			});
	};

	const getScheduleReportsHandler = async (initialPage) => {
		const currentPage = initialPage || page;
		if (currentPage === 1) {
			setPage(1);
		}
		setLoading(true);
		const token = await getAccessTokenSilently();

		const payload = {
			filter: {
				eventTypeID:
					selectedFilter?.eventType?.length > 0 && !selectedFilterAll?.eventType
						? selectedFilter?.eventType
						: undefined,
				groupID: selectedFilter?.group?.length > 0 && !selectedFilterAll?.group ? selectedFilter?.group : undefined,
				active1: selectedFilter?.active?.length > 0 && !selectedFilterAll?.active ? selectedFilter?.active : undefined,
			},
		};

		PastoralService.getScheduleReports({ token, page: currentPage, limit: 30, payload })
			.then((res) => {
				if (res) {
					setReportListing(res?.reports ?? []);
					setScheduleReportTotal(res?.total ?? 0);
				}
			})
			.catch((err) => {
				toastr.error("Error", err?.data?.message || "Something went wrong");
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const onEventTypeFilterChange = (name, value) => {
		if (value === "all") {
			setSelectedFilter({
				...selectedFilter,
				[name]: selectedFilterAll?.[name] ? [] : eventTypes.map((item) => item.value),
			});
			setSelectedFilterAll({
				...selectedFilterAll,
				[name]: !selectedFilterAll?.[name],
			});
			return;
		}
		setSelectedFilterAll({
			...selectedFilterAll,
			[name]: false,
		});
		if (selectedFilter?.[name]?.includes(value)) {
			setSelectedFilter({
				...selectedFilter,
				[name]: selectedFilter?.[name]?.filter((item) => item !== value),
			});
		} else {
			setSelectedFilter({
				...selectedFilter,
				[name]: [...selectedFilter?.[name], value],
			});
		}
	};

	const debounceCall = useCallback(
		debounce(() => {
			if (selectedFilter) {
				getScheduleReportsHandler(1);
			}
		}, 500),
		[selectedFilter, selectedFilterAll]
	);

	useEffect(() => {
		const selectedFilterValues = Object.values(selectedFilter);
		const isFilterSelected = selectedFilterValues.some((item) => item.length > 0);
		if (isFilterSelected) {
			debounceCall();
		} else {
			getScheduleReportsHandler(1);
		}
		return () => debounceCall.cancel();
	}, [selectedFilter]);

	useEffect(() => {
		getGroupsHandler();
		getFrequencyHandler();
	}, []);

	const openViewGroup = () => {
		setOpenViewGroupDrawer(true);
	};

	const closeViewGroup = () => {
		setOpenViewGroupDrawer(false);
	};

	const openAddGroup = (group) => {
		setSelectedGroup(group || null);
		setOpenAddGroupModal(true);
	};

	const closeAddGroup = () => {
		setSelectedGroup(null);
		setOpenAddGroupModal(false);
	};

	const openCreateSchedule = (row) => {
		setSelectedSchedule(row ? row : null);
		setOpenCreateScheduleModal(true);
	};

	const closeCreateSchedule = () => {
		setSelectedSchedule(null);
		setOpenCreateScheduleModal(false);
	};

	const openConfirmDeleteReport = (report) => {
		setOpenDeleteConfirmModal({
			open: true,
			type: "report",
			message: "Are you sure?",
			description: "Do you want to delete this schedule?",
			isLoading: false,
		});
		setSelectedSchedule(report);
	};


	const openConfirmDeleteGroup = (group) => {
		setOpenDeleteConfirmModal({
			open: true,
			type: "group",
			message: "Are you sure?",
			description: "Do you want to delete this group?",
			isLoading: false,
		});
		setSelectedGroup(group);
	};

	const closeConfirmDelete = () => {
		setOpenDeleteConfirmModal({
			open: false,
			type: "",
			message: "",
			description: "",
			isLoading: false,
		});
		setSelectedGroup(null);
		setSelectedSchedule(null);
	};

	const onConfirmDelete = async () => {
		if (openDeleteConfirmModal.type === "group") {
			onDeleteGroup();
		}
		if (openDeleteConfirmModal.type === "report") {
			onDeleteReport();
		}
	}

	const onDeleteReport = async () => {
		setOpenDeleteConfirmModal({
			...openDeleteConfirmModal,
			isLoading: true,
		});
		const token = await getAccessTokenSilently();
		const reportId = selectedSchedule?.id;
		PastoralService.deleteScheduleReports({ token, reportId, })
			.then((res) => {
				if (res) {
					toastr.success("Success", "Schedule deleted successfully");
					getScheduleReportsHandler();
				}
			})
			.catch((err) => {
				toastr.error("Error", err?.data?.message || "Something went wrong");
			})
			.finally(() => {
				closeConfirmDelete();
			});
	};

	const onDeleteGroup = async () => {
		setOpenDeleteConfirmModal({
			...openDeleteConfirmModal,
			isLoading: true,
		});
		const token = await getAccessTokenSilently();
		PastoralService.deleteGroup({ token, groupId: selectedGroup?.value })
			.then((res) => {
				if (res) {
					toastr.success("Success", "Group deleted successfully");
					getGroupsHandler();
				}
			})
			.catch((err) => {
				toastr.error("Error", err?.data?.message || "Something went wrong");
			})
			.finally(() => {
				closeConfirmDelete();
			});
	};

	const eventTypesOptions = useMemo(() => {
		return [
			{
				label: "All",
				value: "all",
			},
			...eventTypes,
		];
	}, [eventTypes]);

	const groupsOptions = useMemo(() => {
		return [
			{
				label: "All",
				value: "all",
			},
			...groupsList,
		];
	}, [groupsList]);

	const closeModal = () => {
    setConfirmClose(true);
  }
  const confirmCloseHandler = () => {
    setConfirmClose(false);
		closeCreateSchedule();
  }

	const closeAddGroupHandler = () => {
		setConfirmClose(false);
		closeAddGroup();
	}
	

	return (
		<div className=" p-0 pastoral-task-automation">
			<div className="user__table__header mb-5">
				<div className="right__section table-header-buttons">
					<div className="mx-5 pastoral-task-automation__view-group" onClick={openViewGroup}>
						<span className="plus-icon">{""}</span>
						<span>View Groups</span>
					</div>
					<Button
              variant="primary"
							onClick={() => openCreateSchedule()}
              style={{ marginLeft: 10 }}
              size="md"
          >
              <PlusIcon />
              <span>New Scheduled Update</span>
          </Button>
				</div>
			</div>
			<div>
				<TableContainer className="scroll-table">
					<Table class="main-table" aria-label="collapsible table" sx={{ minWidth: 750 }}>
						<TableHead className="charges-table sticky-table-header">
							<TableRow>
								<TableCellHead
									component="th"
									scope="row"
									padding="none"
									className="status-heads"
									align="left"
									key="event"
								>
									<div className="d-flex h-100 justify-content-start align-items-center" style={{ gap: "10px" }}>
										EVENT TYPE
										<TableColumnFilter
											options={eventTypesOptions}
											onChange={(value) => onEventTypeFilterChange("eventType", value)}
											value={selectedFilter?.eventType}
											allSelected={selectedFilterAll?.eventType}
										/>
									</div>
								</TableCellHead>
								<TableCellHead align="left" className="class__header__cell year__head">
									<div className="d-flex h-100 justify-content-start align-items-center" style={{ gap: "10px" }}>
										GROUP
										<TableColumnFilter
											options={groupsOptions}
											onChange={(value) => onEventTypeFilterChange("group", value)}
											value={selectedFilter?.group}
											allSelected={selectedFilterAll?.group}
										/>
									</div>
								</TableCellHead>
								<TableCellHead align="left" className="class__header__cell registration__head">
									<div>PARTICIPANTS</div>
								</TableCellHead>
								<TableCellHead align="left" className="class__header__cell ageRane__head">
									<div>FREQUENCY</div>
								</TableCellHead>
								<TableCellHead align="left" className="class__header__cell participants__head">
									<div className="d-flex h-100 justify-content-start align-items-center" style={{ gap: "10px" }}>
										ACTIVE
										<TableColumnFilter
											options={activeOptions}
											onChange={(value) => onEventTypeFilterChange("active", value)}
											value={selectedFilter?.active}
											allSelected={selectedFilterAll?.active}
										/>
									</div>
								</TableCellHead>
								<TableCellHead align="left" className="class__header__cell total__cost">
									<div>UPCOMING</div>
								</TableCellHead>
								<TableCellHead align="left" className="class__header__cell total__cost">
									<div>UPDATES</div>
								</TableCellHead>
								<TableCellHead align="left" className="class__header__cell sticky-header-column" />
							</TableRow>
						</TableHead>
						<ScheduleReportTableBody
							loading={loading}
							scheduleList={reportListing}
							onDeleteSchedule={openConfirmDeleteReport}
							openCreateSchedule={openCreateSchedule}
						/>
					</Table>
				</TableContainer>
			</div>
			<div className="pagination-custom mt-5">
				<Pagination
					count={Math.ceil(scheduleReportTotal / 30)}
					color="primary"
					variant="outlined"
					page={page}
					onChange={(e, value) => {
						setPage(value);
						getScheduleReportsHandler(value);
					}}
				/>
			</div>
			<RightDrawer open={openViewGroupDrawer} onClose={closeViewGroup}>
				<ViewGroup
					closeDrawer={closeViewGroup}
					openAddGroup={openAddGroup}
					groupsList={groupsList}
					groupLoading={getGroupsLoading}
					onDeleteGroupClick={openConfirmDeleteGroup}
				/>
			</RightDrawer>
			<MyModal className="add-task-automation-modal" open={openAddGroupModal} onClose={closeModal}>
				<AddGroup
					closeDrawer={closeModal}
					handleClose={closeAddGroup}
					fetchGroups={getGroupsHandler}
					groupData={selectedGroup || null}
					type={selectedGroup ? "edit" : "create"}
				/>
			</MyModal>
			<MyModal className="add-task-automation-modal" open={openCreateScheduleModal} onClose={closeModal}>
				<AddNewReport
					closeModal={closeModal}
					handleClose={closeCreateSchedule}
					eventTypes={eventTypes}
					eventTypesLoadings={getEventTypesLoading}
					openAddGroup={openAddGroup}
					groupsList={groupsList}
					groupLoading={getGroupsLoading}
					frequencyList={frequencyList}
					frequencyLoading={getFrequencyLoading}
					fetchScheduleReports={() => getScheduleReportsHandler(1)}
					type={selectedSchedule ? "edit" : "create"}
					scheduleData={selectedSchedule}
				/>
			</MyModal>
			<AlertDialog
				open={openDeleteConfirmModal.open}
				onDecline={closeConfirmDelete}
				onAccept={onConfirmDelete}
				title={openDeleteConfirmModal.message}
				description={openDeleteConfirmModal.description}
				disabled={openDeleteConfirmModal.isLoading}
			/>
			<AlertDialog
				open={confirmClose}
				onDecline={() => {
					setConfirmClose(false);
				}}
				onAccept={openAddGroupModal ? closeAddGroupHandler : confirmCloseHandler}
				title={"Are you sure?"}
				description={"Do you want to discard the changes you made?"}
			/>
		</div>
	);
};

export default ScheduleReports;
